import PropTypes from "prop-types";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { FormSelect } from "components/BaseForm";

// Mirrors HOLD_MESSAGE_MAX_LENGTH in twilio_phone_number_repository.py.
export const HOLD_MESSAGE_MAX_LENGTH = 1000;

/**
 * Language and voice selects for a hold message spoken with Twilio <Say>.
 * Only the voices of the selected language are offered.
 */
const HoldMessageVoiceFields = ({
  languageName,
  voiceName,
  languages,
  disabled,
}) => {
  const { setValue } = useFormContext();
  const selectedLanguage = useWatch({ name: languageName });

  const languageOptions = useMemo(
    () => languages.map(({ code, label }) => ({ value: code, text: label })),
    [languages]
  );
  const voiceOptions = useMemo(
    () =>
      (
        languages.find(({ code }) => code === selectedLanguage)?.voices ?? []
      ).map(({ value, label }) => ({ value, text: label })),
    [languages, selectedLanguage]
  );

  return (
    <>
      <FormSelect
        name={languageName}
        label="Language"
        options={languageOptions}
        placeholder="Select language"
        clearable
        disabled={disabled}
        // A voice only pronounces its own language, so a new language
        // invalidates the voice chosen for the previous one.
        onValueChange={() => setValue(voiceName, null)}
      />
      <FormSelect
        name={voiceName}
        label="Voice"
        options={voiceOptions}
        placeholder={
          selectedLanguage ? "Select voice" : "Select a language first"
        }
        disabled={disabled || !selectedLanguage}
      />
    </>
  );
};

HoldMessageVoiceFields.propTypes = {
  languageName: PropTypes.string.isRequired,
  voiceName: PropTypes.string.isRequired,
  languages: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
};

export default HoldMessageVoiceFields;
